import React from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import Button from '@atlaskit/button';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadType'. Did you mean to use 'import HeadType from "@atlaskit/dynamic-table"' instead? | TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'RowType'. Did you mean to use 'import RowType from "@atlaskit/dynamic-table"' instead?
import type { HeadType, RowType } from '@atlaskit/dynamic-table';
import { Field } from '@atlaskit/form';
import { token } from '@atlaskit/tokens';
import { SEARCH_BY } from '../../../common/summary/constants.tsx';
import type { ChartData, SeriesDataEntity } from '../../../common/summary/types.tsx';
import { getFormattedLabel, type FormatMessage } from '../../../common/summary/utils';
import { LabelWrapper } from '../../../common/ui/label/index.tsx';
import messages from './messages';

export type HighlightPointEvent = {
	type?: string;
	seriesIndex?: number;
	seriesName?: string;
	dataIndex?: number;
	name?: string;
	data?: SeriesDataEntity;
};

export const createHead = (
	{ chartSeries }: ChartData,
	{
		formatMessage,
	}: {
		formatMessage: FormatMessage;
	},
): HeadType => {
	const cells = chartSeries.map(({ seriesIndex, name }) => ({
		key: `${seriesIndex}`,
		content: name,
	}));
	cells.unshift({ key: 'date-column', content: formatMessage(messages.dateColumnLabel) });
	return { cells };
};

export const LinkedButton = ({
	seriesIndex,
	dataIndex,
	data,
	value,
	valueType,
	onClick,
	isDisabled,
	formatMessage,
}: {
	seriesIndex: number;
	dataIndex: number;
	value: string;
	valueType: string;
	data: SeriesDataEntity;
	onClick: (arg1: HighlightPointEvent) => void;
	isDisabled?: boolean;
	formatMessage: FormatMessage;
}) => (
	<Button
		isDisabled={isDisabled}
		appearance="link"
		onClick={() => {
			onClick({ seriesIndex, dataIndex, data });
		}}
	>
		<ButtonLabel data-test-id="servicedesk-reports-common.ui.table.summary-table.button-label">
			{getFormattedLabel(valueType, value, formatMessage)}
		</ButtonLabel>
	</Button>
);

export const createRows = (
	{ chartSeries, searchBy }: ChartData,
	{
		formatMessage,
		onClick,
		isDisabled,
	}: {
		formatMessage: FormatMessage;
		onClick: (arg1: HighlightPointEvent) => void;
		isDisabled?: boolean;
	},
): RowType[] =>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	chartSeries.reduce<Array<any>>((accumulator, { name, data, seriesIndex }) => {
		data.forEach((pointData) => {
			const { id, expandedDate, value, valueType, dataIndex } = pointData;
			const row = accumulator.find(({ key }) => key === `${id}`);
			if (!row) {
				const label =
					searchBy === SEARCH_BY.weekly
						? formatMessage(messages.weekStartingLabel, {
								expandedDate,
							})
						: expandedDate;
				const cells = [
					{
						key: `${name}-${expandedDate}`,
						content: (
							<LabelWrapper>
								<Field name="dateLabel" label={label}>
									{/* @ts-expect-error - TS2322 - Type '(...args: any[]) => void' is not assignable to type 'ReactNode'. */}
									{() => noop}
								</Field>
							</LabelWrapper>
						),
					},
					{
						key: `${name}-${id}`,
						content: (
							<LinkedButton
								isDisabled={isDisabled}
								seriesIndex={seriesIndex}
								dataIndex={dataIndex}
								value={value}
								valueType={valueType}
								data={pointData}
								onClick={onClick}
								formatMessage={formatMessage}
							/>
						),
					},
				];
				accumulator.push({ key: `${id}`, cells });
			} else {
				row.cells.push({
					key: `${name}-${id}`,
					content: (
						<LinkedButton
							isDisabled={isDisabled}
							seriesIndex={seriesIndex}
							dataIndex={dataIndex}
							value={value}
							valueType={valueType}
							data={pointData}
							onClick={onClick}
							formatMessage={formatMessage}
						/>
					),
				});
			}
		});
		return accumulator;
	}, []);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonLabel = styled.span({
	padding: `${token('space.025', '2px')} ${token('space.025', '2px')}`,
});
